import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const navigate = useNavigate();
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (tries.current >= MAX_TRIES) {
        setState("timeout");
        return;
      }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[#FF4500] animate-spin" />
          <h1 className="font-display font-black tracking-tighter text-3xl mt-6">Confirming payment…</h1>
          <p className="text-neutral-500 mt-2">Hang tight, this only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#FF4500]" />
          <h1 className="font-display font-black tracking-tighter text-4xl mt-6">Booking confirmed</h1>
          <p className="text-neutral-600 mt-3">Payment received. Your space is locked in — message the host to sort out load-in.</p>
          <Button onClick={() => navigate("/trips")} className="mt-8 bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-8" data-testid="go-to-trips-btn">
            View my trips
          </Button>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-neutral-400" />
          <h1 className="font-display font-black tracking-tighter text-3xl mt-6">
            {state === "timeout" ? "Still processing" : "Something went wrong"}
          </h1>
          <p className="text-neutral-600 mt-3">
            {state === "timeout"
              ? "Your payment is taking longer than expected. Check My Trips shortly — it will update automatically."
              : "We couldn't confirm this payment. If you were charged, it will appear in My Trips soon."}
          </p>
          <Button onClick={() => navigate("/trips")} className="mt-8 bg-[#0A0A0A] text-white rounded-full h-12 px-8">
            Go to my trips
          </Button>
        </>
      )}
    </div>
  );
}
